import { useState, useEffect } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { TrendingUp, TrendingDown, Minus, LineChart as LineIcon } from "lucide-react";
import api from "../lib/api";

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch { return ""; }
}

function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-zinc-900/95 backdrop-blur-md border border-zinc-800 rounded-lg px-3 py-2 shadow-xl">
      <p className="text-[10px] text-zinc-500 uppercase tracking-wider">{p.label}</p>
      <p className="font-mono text-sm font-bold text-emerald-400">${p.price?.toFixed(2)}</p>
    </div>
  );
}

function TrendBadge({ data }) {
  if (!data || data.length < 2) return null;
  const first = data[0].price;
  const last = data[data.length - 1].price;
  const delta = last - first;
  const pct = first > 0 ? (delta / first) * 100 : 0;
  const positive = delta > 0;
  const flat = Math.abs(delta) < 0.01;
  const Icon = flat ? Minus : positive ? TrendingUp : TrendingDown;
  const color = flat ? "text-zinc-400" : positive ? "text-emerald-400" : "text-rose-400";
  return (
    <span data-testid="trend-badge" className={`flex items-center gap-1 text-xs font-medium ${color}`}>
      <Icon className="w-3.5 h-3.5" />
      {positive ? "+" : ""}{delta.toFixed(2)} ({pct.toFixed(1)}%)
    </span>
  );
}

export default function PriceHistoryChart({ releaseId }) {
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [current, setCurrent] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get(`/market/trends/${releaseId}`);
        if (!active) return;
        setHistory(data.history || []);
        setCurrent(data.current_value);
      } catch { /* silent */ }
      finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [releaseId]);

  if (loading) return <div className="h-48 bg-zinc-900/40 rounded-xl animate-pulse" />;

  const chartData = history.map((h) => ({
    label: formatDate(h.date),
    price: h.price,
  }));

  return (
    <div data-testid="price-history-chart" className="bg-zinc-900/60 backdrop-blur-xl border border-white/5 rounded-xl p-5">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <LineIcon className="w-4 h-4 text-rose-500" />
          <h3 className="font-heading text-lg font-semibold text-zinc-100">Price History</h3>
        </div>
        <div className="flex items-center gap-3">
          {current != null && <span className="font-mono text-sm text-zinc-400">Now: <span className="text-emerald-400 font-bold">${current.toFixed(2)}</span></span>}
          <TrendBadge data={chartData} />
        </div>
      </div>
      {chartData.length >= 2 ? (
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 8, right: 16, left: -16, bottom: 0 }}>
              <defs>
                <linearGradient id="priceGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={1} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0.4} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: "#71717a", fontSize: 11 }} axisLine={{ stroke: "#27272a" }} tickLine={false} />
              <YAxis tick={{ fill: "#71717a", fontSize: 11 }} axisLine={{ stroke: "#27272a" }} tickLine={false} domain={["auto", "auto"]} />
              <Tooltip content={<ChartTooltip />} />
              <Line type="monotone" dataKey="price" stroke="url(#priceGrad)" strokeWidth={2} dot={{ fill: "#10b981", r: 3 }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="py-8 text-center">
          <p className="text-sm text-zinc-500">
            {chartData.length === 1
              ? "Only one data point tracked so far. Chart will populate as new AI estimates arrive."
              : "No price history yet. Run an AI market refresh to start tracking."}
          </p>
        </div>
      )}
    </div>
  );
}
